import { z } from 'zod'

// Org-level invoice defaults, stored as system-preference JSON under
// INVOICE_SETTINGS_KEYS. Field names match the values organizations have
// already saved from the invoice Settings tab — numeric-ish fields stay
// strings for backward compatibility with that stored data.

export const INVOICE_SETTINGS_KEYS = {
  SETTINGS: 'invoice.settings',
  PAYMENTS: 'invoice.payments',
} as const

export const InvoiceModuleSettingsSchema = z.object({
  defaultCurrency: z.string().default('USD'),
  defaultPaymentTerms: z.string().default('Net 30'),
  invoicePrefix: z.string().default('INV-'),
  nextInvoiceNumber: z.string().default('1001'),
  defaultTaxRate: z.string().default('0'),
  emailEnabled: z.boolean().default(true),
  emailFromName: z.string().default(''),
  emailReplyTo: z.string().default(''),
  emailSubjectTemplate: z
    .string()
    .default('Invoice #{invoice_number} from {company_name}'),
  emailMessageTemplate: z
    .string()
    .default(
      'Hi,\n\nPlease find attached invoice {invoice_number} for {total_amount}, due {due_date}.\n\nThank you,\n{company_name}',
    ),
  acceptOnlinePayments: z.boolean().default(false),
  // Card surcharge percentage offered as a one-click SURCHARGE line in the
  // invoice editor for card-paying clients (invoicing-ops Phase 5). String
  // like the other numeric-ish fields; never applied silently.
  cardSurchargePercent: z.string().default('2.99'),
  paymentMethods: z.array(z.string()).default([]),
  // Gates the payment block on invoice PDFs: the invoice's own payment
  // instructions plus the bank transfer / PayPal details below. On (the
  // default) prints them; off leaves the block out.
  showPaymentInstructions: z.boolean().default(true),
  // Retired, never read: latePaymentReminders + reminderDays (no client
  // reminder job exists) and showCompanyLogo (invoice PDFs print no logo).
  // Orgs may still have them stored; saves keep them via
  // mergeStoredInvoicePreference rather than this schema.
  defaultNotes: z.string().default(''),
  defaultTermsAndConditions: z.string().default(''),
})

export type InvoiceModuleSettings = z.infer<typeof InvoiceModuleSettingsSchema>

export const InvoicePaymentSettingsSchema = z.object({
  acceptBankTransfer: z.boolean().default(true),
  acceptCreditCard: z.boolean().default(false),
  acceptPayPal: z.boolean().default(false),
  acceptCheck: z.boolean().default(true),
  acceptCash: z.boolean().default(false),
  bankName: z.string().default(''),
  bankAccountName: z.string().default(''),
  bankAccountNumber: z.string().default(''),
  bankRoutingNumber: z.string().default(''),
  bankSwiftCode: z.string().default(''),
  paypalEmail: z.string().default(''),
  paymentInstructions: z.string().default(''),
})

export type InvoicePaymentSettings = z.infer<
  typeof InvoicePaymentSettingsSchema
>

export const getDefaultInvoiceModuleSettings = (): InvoiceModuleSettings =>
  InvoiceModuleSettingsSchema.parse({})

export const getDefaultInvoicePaymentSettings = (): InvoicePaymentSettings =>
  InvoicePaymentSettingsSchema.parse({})

/**
 * Parse a stored settings blob tolerantly: unknown/legacy fields are
 * dropped, missing fields fall back to defaults, and a malformed blob
 * degrades to all-defaults instead of throwing.
 */
export function parseInvoiceModuleSettings(
  value: unknown,
): InvoiceModuleSettings {
  const result = InvoiceModuleSettingsSchema.safeParse(value ?? {})
  return result.success ? result.data : getDefaultInvoiceModuleSettings()
}

export function parseInvoicePaymentSettings(
  value: unknown,
): InvoicePaymentSettings {
  const result = InvoicePaymentSettingsSchema.safeParse(value ?? {})
  return result.success ? result.data : getDefaultInvoicePaymentSettings()
}

/**
 * The value to write back for an invoice preference: the stored blob with
 * the edited fields laid over it. The parse helpers drop keys the schemas no
 * longer know, so saving their output alone would erase retired settings an
 * organization still has stored; this keeps them.
 */
export function mergeStoredInvoicePreference<T extends object>(
  stored: unknown,
  next: T,
): Record<string, unknown> & T {
  const base =
    stored && typeof stored === 'object' && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : {}
  return { ...base, ...next }
}

/**
 * The org's payment details as printed on an invoice, one paragraph per
 * method: bank transfer details when bank transfer is accepted, the PayPal
 * address when PayPal is. Methods that are off, or have nothing filled in,
 * print nothing.
 */
export function formatInvoicePaymentDetails(
  payments: InvoicePaymentSettings,
): string[] {
  const paragraphs: string[] = []
  if (payments.acceptBankTransfer) {
    const fields: Array<[label: string, value: string]> = [
      ['Bank', payments.bankName],
      ['Account name', payments.bankAccountName],
      ['Account number', payments.bankAccountNumber],
      ['Routing number', payments.bankRoutingNumber],
      ['SWIFT/BIC', payments.bankSwiftCode],
    ]
    const rows = fields
      .filter(([, value]) => value.trim() !== '')
      .map(([label, value]) => `${label}: ${value.trim()}`)
    if (rows.length > 0) paragraphs.push(['Bank transfer', ...rows].join('\n'))
  }
  const paypalEmail = payments.paypalEmail.trim()
  if (payments.acceptPayPal && paypalEmail) {
    paragraphs.push(`PayPal: ${paypalEmail}`)
  }
  return paragraphs
}

// Variables available in invoice email subject/message templates.
export interface InvoiceTemplateVariables {
  invoice_number: string
  company_name: string
  total_amount: string
  due_date: string
  // `client_name` is the advertised token; `entity_name` still renders so
  // templates saved before the rename keep working.
  client_name: string
  entity_name: string
  po_number: string
}

export const INVOICE_TEMPLATE_VARIABLES: Array<{
  token: keyof InvoiceTemplateVariables
  description: string
}> = [
  { token: 'invoice_number', description: 'The invoice number, e.g. INV-1001' },
  { token: 'company_name', description: 'Your organization name' },
  { token: 'total_amount', description: 'Invoice total with currency' },
  { token: 'due_date', description: 'The invoice due date' },
  { token: 'client_name', description: 'The client being billed' },
  {
    token: 'po_number',
    description: 'The linked authorization PO number, blank when none',
  },
]

/**
 * Substitute {token} placeholders in an invoice email template. Unknown
 * tokens are left as-is so typos are visible rather than silently blank.
 */
export function renderInvoiceTemplate(
  template: string,
  variables: Partial<InvoiceTemplateVariables>,
): string {
  return template.replace(/\{([a-z_]+)\}/g, (match, token: string) => {
    const value = variables[token as keyof InvoiceTemplateVariables]
    return value !== undefined && value !== null ? value : match
  })
}
